import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, FileCheck, Briefcase, BarChart3, Shield, Loader2, Ban, Check, X, ExternalLink, Search, UserCheck, Crown, KeyRound, CreditCard, AlertTriangle, Zap } from "lucide-react";

const StatCard = ({ label, value, icon: Icon, accent = "red" }) => (
  <div className="cw-card p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${accent}-500/15 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${accent}-500`} />
      </div>
      <div>
        <div className="text-2xl font-display font-bold">{value}</div>
        <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      active ? "bg-red-500/15 text-red-500" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
    }`}>
    <Icon className="w-4 h-4" /> {children}
  </button>
);

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [stripeSettings, setStripeSettings] = useState(null);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [userRole, setUserRole] = useState("");
  const [shiftStatus, setShiftStatus] = useState("");

  const isAdmin = user?.role === "admin";

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  };
  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userRole) params.set("role", userRole);
      if (userQuery) params.set("q", userQuery);
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users || []);
    } catch (e) { toast.error("Erro ao carregar usuários"); }
    setLoading(false);
  };
  const loadDocs = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/documents/pending"); setDocs(data.documents || []); }
    catch (e) { toast.error("Erro ao carregar documentos"); }
    setLoading(false);
  };
  const loadShifts = async () => {
    setLoading(true);
    try {
      const params = shiftStatus ? `?status=${shiftStatus}` : "";
      const { data } = await api.get(`/admin/shifts${params}`);
      setShifts(data.shifts || []);
    } catch { toast.error("Erro ao carregar diárias"); }
    setLoading(false);
  };
  const loadStripe = async () => {
    try { const { data } = await api.get("/admin/settings/stripe"); setStripeSettings(data); } catch {}
  };
  const toggleStripe = async () => {
    if (!stripeSettings) return;
    const goingLive = stripeSettings.mode === "test";
    const msg = goingLive
      ? "⚠️ Ativar modo LIVE do Stripe?\n\nA partir de agora TODOS os pagamentos serão REAIS e o dinheiro cai na sua conta bancária. Continuar?"
      : "Voltar para modo TESTE do Stripe? Nenhum pagamento real será processado enquanto estiver em teste.";
    if (!confirm(msg)) return;
    setStripeBusy(true);
    try {
      const { data } = await api.post("/admin/settings/stripe/toggle");
      toast.success(data.mode === "live" ? "Stripe LIVE ativo 🚀" : "Stripe voltou para TESTE");
      loadStripe();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setStripeBusy(false);
  };

  useEffect(() => { if (isAdmin) loadStats(); }, [isAdmin]);
  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "users") loadUsers();
    if (tab === "docs") loadDocs();
    if (tab === "shifts") loadShifts();
    if (tab === "settings") loadStripe();
    // eslint-disable-next-line
  }, [tab, isAdmin]);

  if (!isAdmin) {
    return <div className="text-center py-20 text-zinc-400">Acesso restrito ao administrador.</div>;
  }

  const suspendUser = async (u) => {
    if (!confirm(`${u.is_suspended ? "Reativar" : "Suspender"} ${u.name}?`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/suspend`);
      toast.success(u.is_suspended ? "Usuário reativado" : "Usuário suspenso");
      loadUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reviewDoc = async (d, status) => {
    try {
      await api.post(`/admin/documents/${d.document_id}/review?status_new=${status}`);
      toast.success(status === "approved" ? "Documento aprovado" : "Documento rejeitado");
      loadDocs(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const forceCancel = async (s) => {
    if (!confirm(`Cancelar diária "${s.title}"?`)) return;
    try {
      await api.post(`/admin/shifts/${s.shift_id}/force-cancel`);
      toast.success("Diária cancelada");
      loadShifts(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changePassword = async () => {
    const pw = prompt("Digite sua nova senha (mínimo 8 caracteres):");
    if (!pw) return;
    if (pw.length < 8) return toast.error("Senha muito curta");
    try {
      await api.post("/admin/change-password", { new_password: pw });
      toast.success("Senha alterada! Use a nova no próximo login.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 anim-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-extrabold flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-500" /> Painel Admin
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Bem-vindo, {user.name} — controle total da plataforma.</p>
        </div>
        <button onClick={changePassword} className="cw-btn-outline text-sm inline-flex items-center gap-2" data-testid="btn-change-pw">
          <KeyRound className="w-4 h-4" /> Trocar minha senha
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-2">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} icon={BarChart3} testid="tab-overview">Métricas</TabButton>
        <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={Users} testid="tab-users">Usuários</TabButton>
        <TabButton active={tab === "docs"} onClick={() => setTab("docs")} icon={FileCheck} testid="tab-docs">Documentos {stats?.docs?.pending > 0 && <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-2">{stats.docs.pending}</span>}</TabButton>
        <TabButton active={tab === "shifts"} onClick={() => setTab("shifts")} icon={Briefcase} testid="tab-shifts">Diárias</TabButton>
        <TabButton active={tab === "settings"} onClick={() => setTab("settings")} icon={CreditCard} testid="tab-settings">Financeiro</TabButton>
      </div>

      {tab === "overview" && stats && (
        <div className="space-y-6" data-testid="admin-overview">
          <div>
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Usuários</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Total" value={stats.users.total} icon={Users} />
              <StatCard label="Comércios" value={stats.users.comercios} icon={Briefcase} accent="amber" />
              <StatCard label="Profissionais" value={stats.users.acougueiros} icon={UserCheck} accent="emerald" />
              <StatCard label="Novos 7d" value={stats.users.new_7d} icon={BarChart3} accent="blue" />
              <StatCard label="Verificados" value={stats.users.verified} icon={Check} accent="emerald" />
              <StatCard label="Premium" value={stats.users.premium} icon={Crown} accent="amber" />
              <StatCard label="Suspensos" value={stats.users.suspended} icon={Ban} accent="red" />
            </div>
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Diárias</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Total" value={stats.shifts.total} icon={Briefcase} />
              <StatCard label="Abertas" value={stats.shifts.open} icon={Briefcase} accent="emerald" />
              <StatCard label="Aceitas" value={stats.shifts.accepted} icon={Briefcase} accent="blue" />
              <StatCard label="Concluídas" value={stats.shifts.completed} icon={Check} accent="emerald" />
              <StatCard label="Canceladas" value={stats.shifts.cancelled} icon={X} accent="red" />
              <StatCard label="Novas 7d" value={stats.shifts.new_7d} icon={BarChart3} accent="blue" />
            </div>
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Moderação</h3>
            <StatCard label="Documentos pendentes" value={stats.docs.pending} icon={FileCheck} accent="amber" />
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-4" data-testid="admin-users">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={userQuery} onChange={e => setUserQuery(e.target.value)}
                placeholder="Buscar por nome, email, cidade…"
                className="cw-input pl-9" data-testid="input-user-search"
                onKeyDown={e => e.key === "Enter" && loadUsers()} />
            </div>
            <select value={userRole} onChange={e => setUserRole(e.target.value)} className="cw-input" data-testid="select-user-role">
              <option value="">Todos</option>
              <option value="comercio">Comércios</option>
              <option value="acougueiro">Profissionais</option>
              <option value="admin">Admins</option>
            </select>
            <button onClick={loadUsers} className="cw-btn-primary text-sm" data-testid="btn-filter-users">Filtrar</button>
          </div>
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-red-500" /> : (
            <div className="cw-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-500">
                  <tr>
                    <th className="p-3">Nome</th><th className="p-3">Email</th>
                    <th className="p-3">Papel</th><th className="p-3">Cidade</th>
                    <th className="p-3">Status</th><th className="p-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} className="border-t border-zinc-800 hover:bg-zinc-900/60" data-testid={`row-user-${u.user_id}`}>
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-zinc-400">{u.email}</td>
                      <td className="p-3"><span className="badge-pill status-open">{u.role}</span></td>
                      <td className="p-3 text-zinc-400">{u.city || "—"}</td>
                      <td className="p-3">
                        {u.is_suspended ? <span className="badge-pill status-cancelled">Suspenso</span>
                          : u.is_verified ? <span className="badge-pill status-accepted">Verificado</span>
                          : <span className="text-zinc-500">—</span>}
                        {u.is_premium && <span className="ml-1 badge-pill status-open">Premium</span>}
                      </td>
                      <td className="p-3">
                        {u.role !== "admin" && (
                          <button onClick={() => suspendUser(u)} className={u.is_suspended ? "cw-btn-outline text-xs" : "text-red-500 hover:underline text-xs"} data-testid={`btn-suspend-${u.user_id}`}>
                            {u.is_suspended ? "Reativar" : "Suspender"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan="6" className="p-6 text-center text-zinc-500">Nenhum usuário encontrado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "docs" && (
        <div className="space-y-3" data-testid="admin-docs">
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-red-500" /> : docs.length === 0 ? (
            <div className="cw-card p-10 text-center text-zinc-500">Nenhum documento pendente 🎉</div>
          ) : docs.map(d => (
            <div key={d.document_id} className="cw-card p-4 flex flex-wrap items-center gap-4" data-testid={`row-doc-${d.document_id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold">{d.owner?.name} <span className="text-xs text-zinc-500 ml-2">{d.owner?.email}</span></div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{d.doc_type} · enviado em {(d.created_at || "").slice(0, 10)}</div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.document_id}/file`} target="_blank" rel="noopener noreferrer"
                 className="cw-btn-outline text-xs inline-flex items-center gap-1" data-testid={`btn-view-doc-${d.document_id}`}>
                <ExternalLink className="w-3 h-3" /> Ver
              </a>
              <button onClick={() => reviewDoc(d, "approved")} className="cw-btn-primary text-xs inline-flex items-center gap-1" data-testid={`btn-approve-${d.document_id}`}>
                <Check className="w-3 h-3" /> Aprovar
              </button>
              <button onClick={() => reviewDoc(d, "rejected")} className="text-red-500 hover:underline text-xs" data-testid={`btn-reject-${d.document_id}`}>
                <X className="w-3 h-3 inline" /> Rejeitar
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "shifts" && (
        <div className="space-y-4" data-testid="admin-shifts">
          <div className="flex gap-2">
            <select value={shiftStatus} onChange={e => setShiftStatus(e.target.value)} className="cw-input" data-testid="select-shift-status">
              <option value="">Todas</option>
              <option value="open">Abertas</option>
              <option value="negotiating">Negociando</option>
              <option value="accepted">Aceitas</option>
              <option value="completed">Concluídas</option>
              <option value="cancelled">Canceladas</option>
            </select>
            <button onClick={loadShifts} className="cw-btn-primary text-sm" data-testid="btn-filter-shifts">Filtrar</button>
          </div>
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-red-500" /> : (
            <div className="cw-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-500">
                  <tr><th className="p-3">Título</th><th className="p-3">Comércio</th><th className="p-3">Cidade</th><th className="p-3">Valor</th><th className="p-3">Status</th><th className="p-3">Ações</th></tr>
                </thead>
                <tbody>
                  {shifts.map(s => (
                    <tr key={s.shift_id} className="border-t border-zinc-800 hover:bg-zinc-900/60" data-testid={`row-shift-${s.shift_id}`}>
                      <td className="p-3 font-medium">{s.title}</td>
                      <td className="p-3 text-zinc-400">{s.commerce_name}</td>
                      <td className="p-3 text-zinc-400">{s.city || "—"}</td>
                      <td className="p-3 text-red-500 font-semibold">R$ {Number(s.final_rate || 0).toFixed(2)}</td>
                      <td className="p-3"><span className={`badge-pill status-${s.status}`}>{s.status}</span></td>
                      <td className="p-3">
                        {s.status !== "cancelled" && s.status !== "completed" && (
                          <button onClick={() => forceCancel(s)} className="text-red-500 hover:underline text-xs" data-testid={`btn-cancel-shift-${s.shift_id}`}>
                            Cancelar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {shifts.length === 0 && (
                    <tr><td colSpan="6" className="p-6 text-center text-zinc-500">Nenhuma diária.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-4 max-w-3xl" data-testid="admin-settings">
          <div className="cw-card p-6" data-testid="stripe-mode-card">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stripeSettings?.mode === "live" ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
                {stripeSettings?.mode === "live" ? <Zap className="w-6 h-6 text-emerald-500" /> : <CreditCard className="w-6 h-6 text-amber-400" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-lg">Stripe (Pagamentos)</h3>
                  {stripeSettings?.mode === "live"
                    ? <span className="badge-pill status-accepted">LIVE — cobrando de verdade 🚀</span>
                    : <span className="badge-pill status-open">TESTE — nenhum pagamento real</span>}
                </div>
                <p className="text-sm text-zinc-400 mt-2">
                  {stripeSettings?.mode === "live"
                    ? "Todos os cartões inseridos serão cobrados de verdade. O dinheiro cai na sua conta Stripe (e depois na sua conta bancária cadastrada no painel do Stripe)."
                    : "No modo teste, cartões reais NÃO são cobrados. Use `4242 4242 4242 4242` para testar a experiência de compra sem custo. Ative o modo LIVE só quando estiver pronto para receber dinheiro real."}
                </p>
              </div>
              <button onClick={toggleStripe}
                disabled={stripeBusy || (stripeSettings?.mode === "test" && !stripeSettings?.live_keys_configured)}
                className={stripeSettings?.mode === "live" ? "cw-btn-outline text-sm" : "cw-btn-primary text-sm"}
                data-testid="btn-toggle-stripe">
                {stripeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (stripeSettings?.mode === "live" ? "Voltar p/ TESTE" : "Ativar LIVE")}
              </button>
            </div>

            {stripeSettings && !stripeSettings.live_keys_configured && (
              <div className="mt-4 p-3 rounded-lg border border-amber-800/50 bg-amber-950/30 flex gap-3 items-start" data-testid="stripe-keys-missing">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200">
                  <b>Chaves LIVE ainda não configuradas.</b><br/>
                  Antes de ativar o modo LIVE, você precisa criar uma conta no <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="underline">dashboard.stripe.com</a>, pegar as chaves `sk_live_...` / `pk_live_...` / `whsec_...` e me passar aqui pra eu adicionar no `.env`. Só depois disso este botão fica habilitado.
                </div>
              </div>
            )}
            {stripeSettings && !stripeSettings.webhook_secret_configured && (
              <div className="mt-3 p-3 rounded-lg border border-zinc-800 bg-zinc-950 flex gap-3 items-start">
                <AlertTriangle className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                <div className="text-xs text-zinc-400">
                  Webhook Secret também não configurado. Sem ele o Stripe não consegue avisar seu app quando um pagamento é confirmado — assinaturas não serão ativadas automaticamente.
                </div>
              </div>
            )}
          </div>

          <div className="cw-card p-6">
            <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-red-500" /> Como o dinheiro flui
            </h3>
            <ol className="text-sm text-zinc-300 space-y-2 list-decimal list-inside">
              <li>Comércio (açougue/supermercado) se cadastra → <b>14 dias grátis</b> para testar.</li>
              <li>Após o trial, Stripe cobra automaticamente <b className="text-red-500">R$ 29,90/mês</b> no cartão dele.</li>
              <li>Dinheiro cai na <b>sua conta Stripe</b> e é repassado para seu banco a cada 7 dias.</li>
              <li>Pagamento das diárias é <b>direto entre comércio e açougueiro</b> (dinheiro/PIX) — seu app só registra e não pega comissão.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
